import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Popover,
  Switch,
  TextField,
  useMediaQuery,
} from '@mui/material';
import PlusIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import { useTranslation } from 'react-i18next';
import WidgetCard from '../../../../components/Cards/WidgetCard/WidgetCard';
import { createCountry } from '../../../../services/api/CountryApi';
import { useSelector } from 'react-redux';

const AddNewCountryPopover = ({ reduxSliceName = 'countrySearch' }) => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);

  const filterNameRef = useRef(null);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const Countries = useSelector((state) => state[reduxSliceName].dataRowItems);
  const isMediumUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const [open, setOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (!open) {
      if (formRef.current) {
        formRef.current.resetForm();
      }
    }

    if (filterNameRef.current) {
      filterNameRef.current.focus();
    }
  }, [open]);

  const popoverContent = (
    <Popover
      anchorEl={anchorRef.current}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      keepMounted
      onClose={handleClose}
      open={open}
      slotProps={{
        paper: {
          sx: { width: 320 },
        }
      }}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          countryName: '',
          countryCode: '',
          countryNumberCode: '',
          active: isActive,
        }}
        validationSchema={Yup.object().shape({
          countryCode: Yup.mixed()
            .required('Country code is required')
            .test(
              'maxLength',
              'Maximum 2 characters allowed',
              (value) => (typeof value === 'string' ? value.length <= 2 : true)
            ),
          countryName: Yup.mixed()
            .required('Country Name is required')
            .test(
              'maxLength',
              'Maximum 50 characters allowed',
              (value) => (typeof value === 'string' ? value.length <= 50 : true)
            ),
          countryNumberCode: Yup.mixed()
            .test(
              'maxLength',
              'Maximum 3 characters allowed',
              (value) => (typeof value === 'string' ? value.length <= 3 : true)
            ),
        })}
        onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
          try {
            const payload = {
              CountryCode: values.countryCode,
              CountryName: values.countryName,
              CountryNumericCode: values.countryNumberCode,
              Active: values.active
            };

            const isDuplicate = Countries.some(
              (country) => country.CountryCode === payload.CountryCode || country.CountryName.toLowerCase() === payload.CountryName.toLowerCase()
            );

            if (isDuplicate) {
              setErrors({ countryCode: t('Record already exists'), countryName: t('Record already exists') });
              toast.error(t('Record already exists'));
              setSubmitting(false);
              return;
            }
            const response = await createCountry(payload);
            if (response.IsSuccess) {
              resetForm();
              setStatus({ success: true });
              toast.success(t('Toast_SavedSuccessfully'));
              handleClose();
              navigate(`/country/${values.countryCode}/edit`);
            } else {
              toast.error(response.Message || t('An error occurred'));
              setStatus({ success: false });
              setErrors({ submit: response.Message || t('An error occurred') });
            }
          } catch (err) {
            console.error(err);
            const errorMessage = err.message || t('An unexpected error occurred');
            toast.error(errorMessage);
            setStatus({ success: false });
            setErrors({ submit: errorMessage });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <WidgetCard
              sx={{ borderRadius: 0 }}
              title={t('Title_Country')}
              icon={<PermDataSettingIcon color="action" />}
              body={
                <>
                  <Box sx={{ py: 1.5 }}>
                    <TextField
                      error={Boolean(touched.countryCode && errors.countryCode)}
                      fullWidth
                      helperText={touched.countryCode && errors.countryCode}
                      label={t('Label_CountryCode')}
                      name="countryCode"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.countryCode}
                      variant="outlined"
                      size="small"
                      inputRef={filterNameRef}
                      slotProps={{
                        htmlInput: { maxLength: 2 }
                      }}
                    />
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <TextField
                      error={Boolean(touched.countryName && errors.countryName)}
                      fullWidth
                      helperText={touched.countryName && errors.countryName}
                      label={t('Label_CountryName')}
                      name="countryName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.countryName}
                      variant="outlined"
                      size="small"
                      slotProps={{
                        htmlInput: { maxLength: 50 }
                      }}
                    />
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <TextField
                      error={Boolean(touched.countryNumberCode && errors.countryNumberCode)}
                      fullWidth
                      helperText={touched.countryNumberCode && errors.countryNumberCode}
                      label={t('Label_CountryNumberCode')}
                      name="countryNumberCode"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.countryNumberCode}
                      variant="outlined"
                      size="small"
                      slotProps={{
                        htmlInput: { maxLength: 3 }
                      }}
                    />
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={() => setIsActive(!isActive)}
                          name="active"
                          size="small"
                        />
    }
                      label="Active"
                    />
                  </Box>
                </>
              }
            />
            <Divider />
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'row-reverse' }}>
              <Button
                color="primary"
                type="submit"
                variant="contained"
                size="small"
                startIcon={<SaveIcon fontSize="small" />}
                loading={isSubmitting}
                disabled={isSubmitting}
                fullWidth={!isMediumUp}
              >
                {t('Btn_Save')}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Popover>
  );

  return (
    <>
      <Button
        ref={anchorRef}
        color="primary"
        variant="contained"
        startIcon={<PlusIcon fontSize="small" />}
        sx={{ ml: 1 }}
        onClick={handleOpen}
        size="small"
      >
        {t('Btn_Add')}
      </Button>

      {popoverContent}
    </>
  );
};

export default AddNewCountryPopover;
